import { useLingui } from '@lingui/react/macro';
import { useFormikContext } from 'formik';
import React, { useCallback, useMemo } from 'react';

import { KitPreview } from 'shared/components/kit';
import { KitElement, KIT_ELEMENT_LABELS } from 'shared/kit/kit-element';
import { KitPattern, KIT_PATTERN_COLOR_COUNT } from 'shared/kit/kit-pattern';
import { KitType } from 'shared/kit/kit-type';
import { SelectOption } from 'ud-form/components/select/component';
import UDFormColorInput from 'ud-form/components/color-input';
import UDFormSelect from 'ud-form/components/select';
import UDText from 'ud-ui/components/text';

import { fitColorsToPattern, getPatternOptions, KitFormValues, toElementStates } from './kit-state';

interface KitFormProps {
  /** Комплект, поля которого сейчас редактируются. */
  kitType: KitType;
  /** Элементы формы, разрешённые для вида спорта команды. */
  elements: KitElement[];
}

/**
 * Поля одного комплекта: узор и цвета каждого элемента плюс живое превью.
 *
 * Значения всех четырёх комплектов лежат в одной Formik-форме уровнем выше
 * (`kit-editor.tsx`), поэтому здесь работа идёт с путями `<kitType>.<element>.*`,
 * а переключение комплекта не размонтирует форму и не теряет правки.
 */
export const KitForm: React.FC<KitFormProps> = ({ kitType, elements }) => {
  const { t, i18n } = useLingui();
  const { values, setFieldValue } = useFormikContext<KitFormValues>();
  const kitValues = values[kitType];
  // Язык в зависимостях — чтобы опции пересобрались при его смене.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const patternOptions = useMemo(() => getPatternOptions(), [i18n.locale]);

  // Смена узора меняет требуемое число цветов: недостающие добираются дефолтом,
  // лишние отбрасываются — иначе поля цвета разъедутся с превью.
  const handlePatternChange = useCallback(
    (element: KitElement, option: SelectOption<KitPattern>) => {
      const colors = kitValues[element]?.colors ?? [];
      setFieldValue(`${kitType}.${element}.colors`, fitColorsToPattern(colors, option.value));
    },
    [kitType, kitValues, setFieldValue],
  );

  return (
    <div className="grid grid-cols-12 gap-6">
      <div className="col-span-7 flex flex-col gap-6">
        {elements.map((element) => {
          const pattern = kitValues[element]?.pattern.value ?? KitPattern.SOLID;
          const colorsCount = KIT_PATTERN_COLOR_COUNT[pattern];

          return (
            <div key={element} className="flex flex-col gap-2">
              <UDText type="subhead">{t(KIT_ELEMENT_LABELS[element])}</UDText>

              {/* Одна строка на элемент: узор и цвета рядом, как было до перевода
                  на `ud-form`. Ширина задаётся `basis-*`, а не `w-*`: обёртка поля
                  (`ud-form/wrapper`) сама вешает `w-full`, и в этой паре побеждает
                  она — поле цвета растягивалось на всю строку, выпихивая соседей. */}
              <div className="flex flex-row flex-nowrap items-start gap-4">
                <UDFormSelect
                  name={`${kitType}.${element}.pattern`}
                  label={t`Узор`}
                  options={patternOptions}
                  containerProps={{ className: 'shrink-0 grow-0 basis-60' }}
                  onChange={(option) => handlePatternChange(element, option as SelectOption<KitPattern>)}
                />

                {Array.from({ length: colorsCount }, (_, index) => {
                  const colorNumber = index + 1;
                  const colorLabel = t`Цвет ${colorNumber}`;

                  return (
                    <UDFormColorInput
                      key={index}
                      name={`${kitType}.${element}.colors[${index}]`}
                      label={colorLabel}
                      title={colorLabel}
                      containerProps={{ className: 'shrink-0 grow-0 basis-36' }}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="col-span-5 flex justify-center">
        <KitPreview elementStates={toElementStates(kitValues)} allowedElements={elements} />
      </div>
    </div>
  );
};

export default KitForm;
